import React from 'react';
import { AlertTriangle, ArrowRight, Check } from 'lucide-react';
import { Button } from '../ui';
import { ProposalChange, ProposalRow, RobbieProposal } from '../../types/robbieProposal';
import { RobbieProposalOutcome } from '../../hooks/useRobbieProposal';

/**
 * A change Robbie proposed on a wizard step, under the message that proposed
 * it: his summary, one row per thing it touches (each change as
 * `label: before → after`, and his reason), and the buyer's Apply / Dismiss.
 * Nothing changes until Apply. Same frame as the BOM card; Apply is cobalt
 * because applying is the buyer's action, not Robbie's.
 *
 * Presentational: state comes from `useRobbieProposal` (wired in
 * `pages/wizard/WizardRobbieProposal`).
 */
export interface RobbieProposalCardProps {
  proposal: RobbieProposal;
  outcome: RobbieProposalOutcome;
  applying: boolean;
  /** Robbie's account of a failed apply, already worded. */
  error: string | null;
  /** False for someone reading along: the change shows, the controls do not. */
  canApply: boolean;
  onApply: () => void;
  onDismiss: () => void;
}

const HEADINGS: Record<RobbieProposal['kind'], string> = {
  sourcing_criteria: 'Proposed criteria',
  context_answers: 'Proposed answers',
  validation_actions: 'Proposed fixes',
  shortlist: 'Proposed shortlist',
  outreach_draft: 'Draft message',
  award_split: 'Proposed award split',
};

const APPLIED: Record<RobbieProposal['kind'], string> = {
  sourcing_criteria: 'Applied to your criteria',
  context_answers: 'Applied to your answers',
  validation_actions: 'Applied to the findings',
  shortlist: 'Applied to your shortlist',
  outreach_draft: "In the composer — press Send when you're happy with it",
  award_split: 'Set on the award builder — confirm the award when ready',
};

const Change: React.FC<{ change: ProposalChange; long: boolean }> = ({ change, long }) => (
  <li className="text-meta leading-[1.45] text-ink-body">
    <span className="text-ink-muted">{change.label}: </span>
    {change.before !== null && (
      <>
        <span className="break-words text-ink-secondary line-through decoration-line-strong">{change.before}</span>
        <ArrowRight aria-label="becomes" className="mx-[3px] inline h-[11px] w-[11px] align-[-1px] text-ink-muted" />
      </>
    )}
    <span className={`break-words font-medium text-ink ${long ? 'whitespace-pre-wrap' : ''}`}>{change.after ?? '—'}</span>
  </li>
);

const Row: React.FC<{ row: ProposalRow; long: boolean }> = ({ row, long }) => (
  <li className="flex flex-col gap-[3px] border-t border-line-divider py-s3 first:border-t-0">
    <span className="break-words text-body-sm font-semibold text-ink">{row.title}</span>
    <ul className="m-0 flex list-none flex-col gap-[2px] p-0">
      {row.changes.map((change, i) => <Change key={i} change={change} long={long} />)}
    </ul>
    {row.reason && <p className="m-0 text-meta leading-[1.45] text-ink-secondary">{row.reason}</p>}
  </li>
);

export const RobbieProposalCard: React.FC<RobbieProposalCardProps> = ({
  proposal, outcome, applying, error, canApply, onApply, onDismiss,
}) => {
  if (outcome.status === 'dismissed') return <p className="m-0 mt-s3 text-meta text-ink-muted">Dismissed</p>;
  if (outcome.status === 'applied') {
    return (
      <p role="status" className="m-0 mt-s3 flex items-center gap-s2 text-meta font-medium text-success-ink">
        <Check className="h-[13px] w-[13px] shrink-0" />
        {APPLIED[proposal.kind]}
      </p>
    );
  }
  return (
    <section
      aria-label={HEADINGS[proposal.kind]}
      className="mt-s3 flex flex-col rounded-[9px] border border-robbie-border bg-surface px-[10px] py-s3"
    >
      <span className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-robbie-ink">{HEADINGS[proposal.kind]}</span>
      {proposal.summary && <p className="m-0 mt-[3px] text-body-sm text-ink-body">{proposal.summary}</p>}
      <ul className="m-0 mt-s2 flex list-none flex-col p-0">
        {proposal.rows.map((row) => <Row key={row.id} row={row} long={proposal.kind === 'outreach_draft'} />)}
      </ul>
      {!canApply && <p className="m-0 mt-s2 text-meta text-ink-muted">Only an editor of this RFQ can apply this.</p>}
      {error && (
        <p role="alert" className="m-0 mt-s2 flex items-start gap-s2 text-meta text-danger-ink">
          <AlertTriangle className="mt-[1px] h-[12px] w-[12px] shrink-0" />
          {error}
        </p>
      )}
      {canApply && (
        <div className="mt-s3 flex flex-wrap items-center gap-s2 border-t border-line-divider pt-s3">
          <Button size="sm" onClick={onApply} disabled={applying}>
            {applying ? 'Applying…' : proposal.applyLabel}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDismiss} disabled={applying}>
            Dismiss
          </Button>
        </div>
      )}
    </section>
  );
};

export default RobbieProposalCard;
